import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { Appearance, useColorScheme } from "react-native";

import {
  breakpoints,
  colors,
  darkColors,
  durations,
  fonts,
  fontSizes,
  iconSizes,
  letterSpacing,
  lightColors,
  lineHeights,
  palette,
  radii,
  shadows,
  spacing,
  textRoles,
  type ColorScheme,
  type ThemeColors,
} from "./tokens";

// Re-export the theme-invariant tokens so modules that only need spacing /
// radii / fonts / etc. can keep importing from "~/styles/theme".
export {
  breakpoints,
  colors,
  palette,
  lightColors,
  darkColors,
  durations,
  fonts,
  fontSizes,
  iconSizes,
  letterSpacing,
  lineHeights,
  radii,
  shadows,
  spacing,
  textRoles,
};
export type { ColorScheme, ThemeColors };

export type ThemePreference = ColorScheme | "system";

const tokens = {
  breakpoints,
  durations,
  fonts,
  fontSizes,
  iconSizes,
  letterSpacing,
  lineHeights,
  radii,
  shadows,
  spacing,
  textRoles,
  palette,
  lightColors,
  darkColors,
} as const;

type ThemeContextValue = {
  preference: ThemePreference;
  scheme: ColorScheme;
  colors: ThemeColors;
  setPreference: (preference: ThemePreference) => void;
  toggleScheme: () => void;
  tokens: typeof tokens;
};

const STORAGE_KEY = "mintstint.theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: PropsWithChildren) {
  const systemScheme = useColorScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>("system");

  useEffect(() => {
    let active = true;
    void AsyncStorage.getItem(STORAGE_KEY).then((stored) => {
      if (!active) return;
      if (stored === "light" || stored === "dark" || stored === "system") {
        setPreferenceState(stored as ThemePreference);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const scheme: ColorScheme =
    preference === "system"
      ? systemScheme === "dark"
        ? "dark"
        : "light"
      : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    void AsyncStorage.setItem(STORAGE_KEY, next);
    if (
      (next === "light" || next === "dark") &&
      typeof Appearance.setColorScheme === "function"
    ) {
      Appearance.setColorScheme(next);
    }
  }, []);

  const toggleScheme = useCallback(() => {
    setPreference(scheme === "dark" ? "light" : "dark");
  }, [scheme, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      scheme,
      colors: scheme === "dark" ? darkColors : lightColors,
      setPreference,
      toggleScheme,
      tokens,
    }),
    [preference, scheme, setPreference, toggleScheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}

/** Safe fallback for modules that may render outside the provider in tests. */
export function useThemeColors(): ThemeColors {
  const context = useContext(ThemeContext);
  const system = useColorScheme();
  if (context) return context.colors;
  return system === "dark" ? darkColors : lightColors;
}

/** Build a value from the active palette, memoised per palette change. */
export function useThemedStyles<T>(factory: (colors: ThemeColors) => T): T {
  const { colors: active } = useTheme();
  return useMemo(() => factory(active), [active, factory]);
}
